import React, { useEffect } from "react";
import { Button, Col, Empty, Form, message, Row, Select, Table, Tag, Typography } from "antd";
import { EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { routes } from "common/routes";
import {
  getPlatformConfigs,
  IPlatformConfig,
  selectPlatformConfigState,
} from "store/platform-config/platformConfigSlice";
import { DEFAULT_VERSION, PLATFORM_OPTIONS } from "./configUtils";

const MESSAGE_KEY = "platform-config-list";
const { Text } = Typography;

const formatDate = (value?: string) =>
  value ? moment(value).format("DD MMM YYYY, hh:mm A") : "-";

/**
 * Entry page of the config panel: pick a platform, see every config version
 * (default first), and jump into editing or creating one.
 */
const PlatformConfigListingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const platform = searchParams.get("platform") ?? undefined;

  const { configs, listStatus, listError } = useAppSelector(selectPlatformConfigState);

  useEffect(() => {
    if (platform) {
      dispatch(getPlatformConfigs(platform));
    }
  }, [platform, dispatch]);

  useEffect(() => {
    if (!platform) return;
    if (listStatus === "loading") {
      message.loading({ content: "Loading configs...", key: MESSAGE_KEY });
    } else if (listStatus === "error") {
      message.error({ content: listError, key: MESSAGE_KEY, duration: 3 });
    } else {
      message.destroy(MESSAGE_KEY);
    }
    return () => message.destroy(MESSAGE_KEY);
  }, [listStatus, listError, platform]);

  const openDetail = (record: IPlatformConfig) => {
    navigate(
      `/${generatePath(routes.PLATFORM_CONFIGS.DETAIL, {
        name: record.name,
        version: record.version,
      })}`
    );
  };

  // Default config first, then newest versions on top.
  const sortedConfigs = [...configs].sort((a, b) => {
    if (a.version === DEFAULT_VERSION) return -1;
    if (b.version === DEFAULT_VERSION) return 1;
    return b.version.localeCompare(a.version, undefined, { numeric: true });
  });

  const columns = [
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      render: (version: string) =>
        version === DEFAULT_VERSION ? (
          <Tag color="gold">DEFAULT</Tag>
        ) : (
          <Tag color="blue">{version}</Tag>
        ),
    },
    {
      title: "Config Keys",
      dataIndex: "config",
      key: "configKeys",
      render: (config: Record<string, unknown>) => {
        const keys = Object.keys(config ?? {});
        return (
          <Text type="secondary">
            {keys.length} {keys.length === 1 ? "key" : "keys"}
          </Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active?: number) =>
        active === 0 ? <Tag color="red">Inactive</Tag> : <Tag color="green">Active</Tag>,
    },
    {
      title: "Modified On",
      dataIndex: "modifiedOn",
      key: "modifiedOn",
      render: formatDate,
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      render: formatDate,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: unknown, record: IPlatformConfig) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => openDetail(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <AppPageHeader title="Platform Configs" />
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              navigate(
                `/${routes.PLATFORM_CONFIGS.CREATE}${platform ? `?platform=${platform}` : ""}`
              )
            }
          >
            New Config Version
          </Button>
        </Col>
      </Row>

      <Form layout="vertical" className="mb-8">
        <Row gutter={24} align="bottom">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Platform" required className="mb-4">
              <Select
                showSearch
                placeholder="Select platform"
                className="w-full"
                value={platform}
                onChange={(value: string) => setSearchParams({ platform: value })}
                options={PLATFORM_OPTIONS.map((name) => ({ value: name, label: name }))}
              />
            </Form.Item>
          </Col>
          {platform && (
            <Col>
              <Form.Item className="mb-4">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => dispatch(getPlatformConfigs(platform))}
                >
                  Refresh
                </Button>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {!platform ? (
        <Row justify="center" align="middle" style={{ minHeight: 300 }}>
          <Col>
            <Empty description="Select a platform to view its configs" />
          </Col>
        </Row>
      ) : (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={sortedConfigs}
              rowKey="id"
              bordered
              size="small"
              loading={listStatus === "loading"}
              pagination={false}
              locale={{ emptyText: "No configs found for this platform" }}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default PlatformConfigListingPage;
